import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import { stateAndCountrySchema } from "~/shared/schemas/location-state-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import {
	getAndValidate,
	postAndValidate,
} from "~/shared/services/api/fetch-client.ts";

import type { UpdateBillingInformationBody } from "../schemas/billing-information-schema.ts";
import { billingInformationSchema } from "../schemas/billing-information-schema.ts";

const updateResponseSchema = z.string();

export function useGetBillingInformation() {
	return useQuery({
		queryKey: ["billingInformation"],
		queryFn: async () => {
			const result = await getAndValidate(
				apiRoutes.GETBILLINGINFORMATION,
				billingInformationSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
	});
}

export function useUpdateBillingInformation() {
	const queryClient = useQueryClient();
	const { showError } = useNotificationActions();

	return useMutation({
		mutationFn: async (body: UpdateBillingInformationBody) => {
			const result = await postAndValidate(
				apiRoutes.UPDATEBILLINGINFORMATION,
				body,
				updateResponseSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["billingInformation"] });
		},
		onError: (error: Error) => {
			console.error(error);
			showError("Failed to save account settings. Please try again later.");
		},
	});
}

export function useStates() {
	return useQuery({
		queryKey: ["states"],
		queryFn: async () => {
			const result = await getAndValidate(
				`${apiRoutes.STATES}?country=USA`,
				z.array(stateAndCountrySchema),
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		staleTime: Infinity,
	});
}
